/* @flow */

import classNames from 'classnames';
import { compose } from 'ramda';
import * as React from 'react';
import { createUseStyles } from 'react-jss';

import styles from './NotificationBanner.style';
import ArrowIcon from 'src/shared/ui/component/icon/ArrowIcon';
import CircledCrossIcon from 'src/shared/ui/component/icon/CircledCrossIcon';
import BaseLink from 'src/shared/ui/component/link/BaseLink';

type Props = {
  layoutClass?: string,
  withWarning?: boolean,
  withError?: boolean,
  href?: string,
  media?: React.Node,
  handleClick?: () => void,
  handleDismiss?: () => void,
  children: React.Node,
};

const useStyles = createUseStyles(styles);

const NotificationBanner = ({
  layoutClass,
  withWarning,
  withError,
  href,
  media,
  handleClick,
  handleDismiss,
  children,
}: Props): React.Node => {
  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles();

  const rootClasses = classNames(classes.root, layoutClass, {
    [classes['root-withWarning']]: withWarning,
    [classes['root-withError']]: withError,
    [classes['root-withMedia']]: media,
    [classes['root-isDismissable']]: handleDismiss,
  });

  /*
   |----------------------------------------------------------------------------
   | Event Handlers
   |----------------------------------------------------------------------------
   */

  const onDismissIconClick = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (handleDismiss) {
      handleDismiss();
    }
  };

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  // TODO: this is an accessibiliy issue, it should be a link. This is a
  // workaround because links cannot be nested.
  const dismissBtn = handleDismiss && (
    <span className={classes.dismiss} onClick={onDismissIconClick}>
      <CircledCrossIcon />
    </span>
  );

  const arrowIcon = href && (
    <span className={classes.arrow}>
      <ArrowIcon right />
    </span>
  );

  const childElt = (
    <React.Fragment>
      {dismissBtn}
      <span className={classes.text}>{children}</span>
      {arrowIcon}
    </React.Fragment>
  );

  return href ? (
    <BaseLink className={rootClasses} href={href} handleClick={handleClick}>
      {childElt}
    </BaseLink>
  ) : (
    <div className={rootClasses}>{childElt}</div>
  );
};

export default compose(React.memo)(NotificationBanner);
